import { Injectable } from '@angular/core';
import OktaAuth, { AccessToken, AuthenticatorKey, IdxStatus, IdxTransaction, Tokens } from '@okta/okta-auth-js';
import { environment } from '@env/environment';
import { AppError } from '@app/core/errors/app-error.model';
import { ErrorMapperService } from '@app/core/errors/error-mapper.service';
import { SESSION_TOKEN_STORAGE_KEY } from '../auth-storage.constants';

const EMAIL_METHOD_TYPE = 'email';
const AUTHENTICATOR_VERIFICATION_DATA_STEP = 'authenticator-verification-data';
const CHALLENGE_AUTHENTICATOR_STEP = 'challenge-authenticator';
const RESET_AUTHENTICATOR_STEP = 'reset-authenticator';
const SELECT_AUTHENTICATOR_AUTHENTICATE_STEP = 'select-authenticator-authenticate';

type RecoveryMethod = {
    id: string;
    key?: string;
    methodType: string;
};

type RecoveryMethodField = {
    name?: string;
    value?: unknown;
    options?: Array<{ value?: unknown }>;
};

type RecoveryMethodOption = {
    value?: { form?: { value?: RecoveryMethodField[] } };
    relatesTo?: {
        id?: string;
        key?: string;
        methods?: Array<{ type?: string }>;
    };
};

type RecoveryAuthenticatorInput = {
    name?: string;
    form?: { value?: RecoveryMethodField[] };
    options?: RecoveryMethodOption[];
};

type RecoveryRemediation = {
    name?: string;
    value?: RecoveryAuthenticatorInput[];
};

type AuthenticatorPayload = {
    id?: string;
    key?: AuthenticatorKey;
    methodType: string;
};

export type LoginResult =
    | { type: 'REDIRECT' }
    | { type: 'MFA_REQUIRED' }
    | { type: 'ERROR'; message: string; appError?: AppError };

@Injectable({
    providedIn: 'root'
})
export class OktaAuthService {
    private readonly oktaAuth = new OktaAuth({
        issuer: environment.okta_config.issuer,
        clientId: environment.okta_config.clientId,
        redirectUri: environment.okta_config.redirectUri,
        pkce: true
    });

    constructor(private readonly errorMapper: ErrorMapperService) {}

    async login(username: string, password: string): Promise<LoginResult> {
        try {
            let transaction = await this.oktaAuth.idx.authenticate({
                username,
                password,
                authenticator: AuthenticatorKey.OKTA_EMAIL
            });

            transaction = await this.ensureEmailChallenge(transaction);

            if (transaction.status === IdxStatus.SUCCESS) {
                this.storeTokens(transaction.tokens);
                return { type: 'REDIRECT' };
            }

            if (transaction.status === IdxStatus.PENDING) {
                return { type: 'MFA_REQUIRED' };
            }

            return this.toLoginError(transaction);
        } catch (err: any) {
            return this.toLoginError(err);
        }
    }

    async handleCallback(): Promise<boolean> {
        const { tokens } = await this.oktaAuth.token.parseFromUrl();
        if (tokens.accessToken && tokens.idToken) {
            this.oktaAuth.tokenManager.setTokens(tokens);
            return true;
        }
        return false;
    }

    async forgotPassword(username: string): Promise<void> {
        try {
            let transaction = await this.oktaAuth.idx.recoverPassword();

            if (transaction.status !== IdxStatus.PENDING) {
                throw this.errorMapper.toAppError(transaction, 'recovery');
            }

            const usernamePayload = { username };
            transaction = await this.oktaAuth.idx.proceed(usernamePayload);
            if (transaction.status !== IdxStatus.PENDING) {
                throw this.errorMapper.toAppError(transaction, 'recovery');
            }

            const recoveryMethod = this.getEmailRecoveryMethod(transaction);
            const authenticatorPayload = {
                step: SELECT_AUTHENTICATOR_AUTHENTICATE_STEP,
                authenticator: recoveryMethod
                    ? { id: recoveryMethod.id }
                    : AuthenticatorKey.OKTA_EMAIL
            };
            transaction = await this.oktaAuth.idx.proceed(authenticatorPayload);
            if (transaction.status !== IdxStatus.PENDING) {
                throw this.errorMapper.toAppError(transaction, 'recovery');
            }

            if (this.hasRecoveryStep(transaction, AUTHENTICATOR_VERIFICATION_DATA_STEP)) {
                const methodType = this.getVerificationMethodType(transaction);
                if (!methodType) {
                    throw this.errorMapper.toAppError(transaction, 'recovery');
                }

                const methodTypePayload = { methodType };
                transaction = await this.oktaAuth.idx.proceed(methodTypePayload);
                if (transaction.status !== IdxStatus.PENDING) {
                    throw this.errorMapper.toAppError(transaction, 'recovery');
                }
            }

            const isChallengeReady = this.hasRecoveryStep(transaction, CHALLENGE_AUTHENTICATOR_STEP);
            if (!isChallengeReady) {
                throw this.errorMapper.toAppError(transaction, 'recovery');
            }
        } catch (err: unknown) {
            throw this.errorMapper.toAppError(err, 'recovery');
        }
    }

    async verifyOtpForLogin(otp: string): Promise<LoginResult> {
        try {
            const transaction = await this.submitLoginOtp(otp);
            if (transaction.status === IdxStatus.SUCCESS) {
                this.storeTokens(transaction.tokens);
                return { type: 'REDIRECT' };
            }
            return this.toOtpError(transaction);
        } catch (err: any) {
            return this.toOtpError(err);
        }
    }

    async verifyOtpForRecovery(otp: string): Promise<boolean> {
        const transaction = await this.submitRecoveryOtp(otp);
        if (transaction.status !== IdxStatus.PENDING) {
            return false;
        }
        if (transaction.nextStep?.name === 'reset-authenticator') {
            return true;
        }
        const nextStepInputs = (transaction.nextStep as any)?.inputs ?? [];
        return nextStepInputs.some((input: any) => input?.name === 'password');
    }

    async resetPassword(newPassword: string): Promise<boolean> {
        const payload = { password: newPassword };
        const transaction = await this.oktaAuth.idx.proceed(payload);
        if (transaction.status === IdxStatus.SUCCESS) {
            this.storeTokens(transaction.tokens);
            return true;
        }
        if (this.isResetAuthenticatorStep(transaction)) {
            throw this.errorMapper.toAppError(transaction, 'reset');
        }
        return false;
    }

    async getAccessToken(): Promise<AccessToken> {
        return await this.oktaAuth.tokenManager.get('accessToken') as AccessToken;
    }

    async logout(): Promise<void> {
        try {
            await this.oktaAuth.idx.cancel();
        } catch {
        }

        try {
            await this.oktaAuth.revokeAccessToken();
        } catch {
        }

        try {
            await this.oktaAuth.revokeRefreshToken();
        } catch {
        }

        try {
            await this.oktaAuth.closeSession();
        } catch {
        }

        this.oktaAuth.tokenManager.clear();
        localStorage.removeItem('access_token');
        localStorage.removeItem('id_token');
        localStorage.removeItem('okta-token-storage');
        localStorage.removeItem('dni');
        localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    }

    private async ensureEmailChallenge(transaction: IdxTransaction): Promise<IdxTransaction> {
        if (transaction.status !== IdxStatus.PENDING) {
            return transaction;
        }

        let current = transaction;
        let authenticatorId = this.getAuthenticatorIdFromNextStep(current);

        if (current.nextStep?.name !== AUTHENTICATOR_VERIFICATION_DATA_STEP) {
            current = await this.oktaAuth.idx.proceed({
                step: SELECT_AUTHENTICATOR_AUTHENTICATE_STEP,
                authenticator: this.buildEmailAuthenticator(authenticatorId)
            });
        }

        if (current.status !== IdxStatus.PENDING) {
            return current;
        }

        if (!authenticatorId) {
            authenticatorId = this.getAuthenticatorIdFromNextStep(current);
        }

        return this.oktaAuth.idx.proceed({
            step: AUTHENTICATOR_VERIFICATION_DATA_STEP,
            authenticator: this.buildEmailAuthenticator(authenticatorId)
        });
    }

    private async submitLoginOtp(otp: string): Promise<IdxTransaction> {
        try {
            const payload = { verificationCode: otp };
            const transaction = await this.oktaAuth.idx.proceed(payload);
            return transaction;
        } catch {
            const fallbackPayload = { otp };
            const transaction = await this.oktaAuth.idx.proceed(fallbackPayload);
            return transaction;
        }
    }

    private async submitRecoveryOtp(otp: string): Promise<IdxTransaction> {
        try {
            const payload = { verificationCode: otp };
            return await this.oktaAuth.idx.proceed(payload);
        } catch {
            const fallbackPayload = { otp };
            return await this.oktaAuth.idx.proceed(fallbackPayload);
        }
    }

    private storeTokens(tokens?: Tokens): void {
        if (!tokens) {
            return;
        }
        this.oktaAuth.tokenManager.setTokens(tokens);
    }

    private toLoginError(error: unknown): LoginResult {
        const appError = this.errorMapper.toAppError(error, 'login');

        return {
            type: 'ERROR',
            message: appError.message,
            appError
        };
    }

    private toOtpError(error: unknown): LoginResult {
        const appError = this.errorMapper.toAppError(error, 'otp');

        return {
            type: 'ERROR',
            message: appError.message,
            appError
        };
    }

    private getEmailRecoveryMethod(transaction: IdxTransaction): RecoveryMethod | null {
        const nextStepInput = (transaction.nextStep?.inputs as RecoveryAuthenticatorInput[] | undefined)
            ?.find(input => input.name === 'authenticator');
        const rawRemediations = (transaction.rawIdxState?.remediation?.value ?? []) as unknown as RecoveryRemediation[];
        const rawInputs = rawRemediations
            .filter(remediation => remediation.name === SELECT_AUTHENTICATOR_AUTHENTICATE_STEP
                || remediation.name === AUTHENTICATOR_VERIFICATION_DATA_STEP)
            .flatMap(remediation => remediation.value ?? [])
            .filter(input => input.name === 'authenticator');
        const authenticatorInputs = [nextStepInput, ...rawInputs]
            .filter((input): input is RecoveryAuthenticatorInput => input !== undefined);
        const availableMethods = authenticatorInputs
            .flatMap(input => input.options ?? [])
            .map(option => this.parseRecoveryMethod(option.value?.form?.value, option))
            .filter((method): method is RecoveryMethod => method !== null);
        const emailMethod = availableMethods.find(method =>
            method.key === String(AuthenticatorKey.OKTA_EMAIL) || method.methodType.toLowerCase() === EMAIL_METHOD_TYPE
        );

        if (emailMethod) {
            return emailMethod;
        }

        return authenticatorInputs
            .map(input => this.parseRecoveryMethod(input.form?.value))
            .find((method): method is RecoveryMethod => method !== null) ?? null;
    }

    private getVerificationMethodType(transaction: IdxTransaction): string | null {
        const inputs = transaction.nextStep?.inputs as RecoveryMethodField[] | undefined;
        return this.getRecoveryMethodFieldValue(inputs, 'methodType', 'methdtype')
            ?? this.getRawVerificationMethodType(transaction)
            ?? transaction.nextStep?.authenticator?.methods?.find(method => method.type)?.type
            ?? null;
    }

    private getRawVerificationMethodType(transaction: IdxTransaction): string | undefined {
        const verificationRemediation = this.getRawRecoveryRemediations(transaction)
            .find(remediation => remediation.name === AUTHENTICATOR_VERIFICATION_DATA_STEP);
        const rawInputs = verificationRemediation?.value ?? [];
        const directMethodType = this.getRecoveryMethodFieldValue(
            rawInputs as unknown as RecoveryMethodField[],
            'methodType',
            'methdtype'
        );
        const authenticatorInput = rawInputs.find(input => input.name === 'authenticator');

        return directMethodType
            ?? this.getRecoveryMethodFieldValue(authenticatorInput?.form?.value, 'methodType', 'methdtype');
    }

    private hasRecoveryStep(transaction: IdxTransaction, step: string): boolean {
        return transaction.nextStep?.name === step
            || this.getRawRecoveryRemediations(transaction).some(remediation => remediation.name === step);
    }

    private getRawRecoveryRemediations(transaction: IdxTransaction): RecoveryRemediation[] {
        return (transaction.rawIdxState?.remediation?.value ?? []) as unknown as RecoveryRemediation[];
    }

    private getAuthenticatorIdFromNextStep(transaction: IdxTransaction): string | null {
        const authenticatorInput = (transaction.nextStep as any)?.inputs?.find((input: any) => input?.name === 'authenticator');
        const optionValue = authenticatorInput?.options?.[0]?.value;
        const directFormValues = authenticatorInput?.form?.value ?? [];
        const optionFormValues = optionValue?.form?.value ?? [];
        const rawValues = (((transaction as any)?.rawIdxState?.remediation?.value ?? []) as any[])
            .find((r: any) => r?.name === AUTHENTICATOR_VERIFICATION_DATA_STEP)?.value ?? [];
        const rawAuthenticator = rawValues.find((v: any) => v?.name === 'authenticator');
        const rawFormValues = rawAuthenticator?.form?.value ?? [];

        const formValues = [...directFormValues, ...optionFormValues, ...rawFormValues];
        const idField = formValues.find((field: any) => field?.name === 'id');
        return idField?.value ?? null;
    }

    private parseRecoveryMethod(fields: RecoveryMethodField[] | undefined, option?: RecoveryMethodOption): RecoveryMethod | null {
        const id = this.getRecoveryMethodFieldValue(fields, 'id') ?? option?.relatesTo?.id;
        const methodType = this.getRecoveryMethodFieldValue(fields, 'methodType', 'methdtype')
            ?? option?.relatesTo?.methods?.find(method => method.type)?.type;

        if (!id || !methodType) {
            return null;
        }

        return {
            id,
            key: option?.relatesTo?.key,
            methodType
        };
    }

    private getRecoveryMethodFieldValue(fields: RecoveryMethodField[] | undefined, ...names: string[]): string | undefined {
        const field = fields?.find(candidate => candidate.name && names.includes(candidate.name));
        const value = field?.value ?? field?.options?.[0]?.value;
        return typeof value === 'string' && value.length > 0 ? value : undefined;
    }

    private buildEmailAuthenticator(recoveryMethod: RecoveryMethod | string | null): AuthenticatorPayload {
        const authenticatorId = typeof recoveryMethod === 'string' ? recoveryMethod : recoveryMethod?.id;
        const methodType = typeof recoveryMethod === 'object' && recoveryMethod
            ? recoveryMethod.methodType
            : EMAIL_METHOD_TYPE;

        return {
            ...(authenticatorId ? { id: authenticatorId } : { key: AuthenticatorKey.OKTA_EMAIL }),
            methodType
        };
    }

    private isResetAuthenticatorStep(transaction: IdxTransaction): boolean {
        if (transaction.nextStep?.name === RESET_AUTHENTICATOR_STEP) {
            return true;
        }

        const rawRemediations = (((transaction as any)?.rawIdxState?.remediation?.value ?? []) as any[]);
        return rawRemediations.some((remediation: any) => remediation?.name === RESET_AUTHENTICATOR_STEP);
    }

}
